import React, { Fragment, useCallback } from "react";

import {
  Box,
  ClickAwayListener,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

import StatusChipComponent from "components/chip/StatusChip.component";
import {
  IconAccessPoint,
  IconRouter,
  IconUsers,
  IconVideoMinus,
  IconVolume,
  IconWifi,
} from "@tabler/icons";
import { IconVolumeOff } from "@tabler/icons";
import { grey } from "@mui/material/colors";
import { useTranslation } from "react-i18next";
import { getTimeZoneStandard } from "utils/timezone";
import { DateTime } from "luxon";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";

const WarningListRow = ({
  item,
  onClickMutate = () => {},
  onClickStation = () => {},
  isVisitor,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);

  const onCopyId = useCallback((id) => {
    if (id) {
      navigator.clipboard.writeText(id);
      setOpen(true);

      setTimeout(() => {
        handleTooltipClose();
      }, 1000);
    }
  }, []);

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const getIconComponent = useCallback(() => {
    const color =
      theme.palette.mode === "dark" ? "white" : theme.palette.primary.main;

    switch (item?.componentType) {
      case "GATEWAY":
        return (
          <Tooltip
            title={`Gateway ${
              item?.componentId ? `(${item?.componentId})` : ""
            }`}
          >
            <IconButton
              size="large"
              sx={{
                color: color,
                cursor: item?.componentId ? "pointer" : "auto",
                "&.MuiIconButton-root:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              <IconAccessPoint />
            </IconButton>
          </Tooltip>
        );
      case "ENVSENSOR":
        return (
          <Tooltip
            title={`${t("environmental monitoring")} ${
              item?.componentId ? `(${item?.componentId})` : ""
            }`}
          >
            <IconButton
              size="large"
              sx={{
                color: color,
                cursor: item?.componentId ? "pointer" : "auto",
                "&.MuiIconButton-root:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              <IconRouter />
            </IconButton>
          </Tooltip>
        );
      case "CAMERA":
        return (
          <Tooltip
            title={`Camera ${
              item?.componentId ? `(${item?.componentId})` : ""
            }`}
          >
            <IconButton
              size="large"
              sx={{
                cursor: item?.componentId ? "pointer" : "auto",
                color: color,
                "&.MuiIconButton-root:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              <IconVideoMinus />
            </IconButton>
          </Tooltip>
        );
      case "PCOUNTER":
        return (
          <Tooltip
            title={`${t("people-counter")} ${
              item?.componentId ? `(${item?.componentId})` : ""
            }`}
          >
            <IconButton
              size="large"
              sx={{
                color: color,
                cursor: item?.componentId ? "pointer" : "auto",
                "&.MuiIconButton-root:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              <IconUsers />
            </IconButton>
          </Tooltip>
        );
      case "WIFI":
        return (
          <Tooltip
            title={`WiFi ${item?.componentId ? `(${item?.componentId})` : ""}`}
          >
            <IconButton
              size="large"
              sx={{
                color: color,
                cursor: item?.componentId ? "pointer" : "auto",
                "&.MuiIconButton-root:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              <IconWifi />
            </IconButton>
          </Tooltip>
        );

      default:
        return;
    }
    // eslint-disable-next-line
  }, [item, theme, t]);

  return (
    <Fragment>
      <TableRow
        sx={{
          backgroundColor:
            item?.isMuted || !item?.isActive
              ? theme.palette.mode === "dark"
                ? "#012133"
                : grey[200]
              : "transparent",
        }}
      >
        {!isVisitor && (
          <TableCell>
            <Tooltip title={t("Monitoring point management")}>
              <IconButton
                color="primary"
                onClick={onClickStation}
                size="small"
                style={{ padding: 0 }}
              >
                <ManageSearchIcon />
              </IconButton>
            </Tooltip>
          </TableCell>
        )}

        <TableCell align="left">{item?.stationId}</TableCell>
        <TableCell align="left">{item?.cluster}</TableCell>
        <TableCell align="left">
          <ClickAwayListener onClickAway={handleTooltipClose}>
            <Tooltip
              PopperProps={{
                disablePortal: true,
              }}
              onClose={handleTooltipClose}
              open={open}
              disableFocusListener
              disableHoverListener
              disableTouchListener
              title={t("ID copied")}
              placement="top"
              onClick={() => onCopyId(item?.componentId)}
            >
              <Box>{getIconComponent()}</Box>
            </Tooltip>
          </ClickAwayListener>
        </TableCell>
        <TableCell align="left">
          <Tooltip title={item?.type}>
            <Typography>{`${item?.type.slice(0, 18)}...`}</Typography>
          </Tooltip>
        </TableCell>
        <TableCell align="left">
          <StatusChipComponent
            status={
              item?.severity !== 0
                ? item?.severity === 40 || item?.severity === -1
                  ? "Critico"
                  : item?.severity === 20
                  ? "Info"
                  : "Attenzione"
                : "Ok"
            }
          />
        </TableCell>

        <TableCell align="left">
          <Box>
            <Box>
              {item?.creationDate !== null
                ? `Inizio: ${DateTime.fromISO(
                    getTimeZoneStandard(item?.creationDate)
                  ).toLocaleString(DateTime.DATETIME_MED, {
                    locale: i18n?.language ? i18n.language : "it",
                  })}`
                : "Inizio: -"}
            </Box>

            <Box>
              {item?.endDate !== null &&
                `Fine: ${DateTime.fromISO(
                  getTimeZoneStandard(item?.endDate)
                ).toLocaleString(DateTime.DATETIME_MED, {
                  locale: i18n?.language ? i18n.language : "it",
                })}`}
            </Box>
          </Box>
        </TableCell>
        <TableCell align="left">
          {item?.isMuted ? (
            <Tooltip title={t("Mute activated")}>
              <IconButton
                size="large"
                onClick={onClickMutate}
                color="secondary"
              >
                <IconVolumeOff
                  style={{
                    fontSize: "1.3rem",
                    // color: theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.primary.main
                  }}
                />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title={t("Mute off")}>
              <IconButton
                size="large"
                onClick={onClickMutate}
                color="secondary"
              >
                <IconVolume
                  style={{
                    fontSize: "1.3rem",
                    // color: theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.primary.main
                  }}
                />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
    </Fragment>
  );
};

export default WarningListRow;
